import { t } from "@lingui/core/macro";
import graphql from "babel-plugin-relay/macro";
import React from "react";
import { useFragment } from "react-relay";

import { Button } from "@whoosh/design-system/src/components/controls";
import { convertCardBrandToIcon } from "@whoosh/design-system/src/components/icons_v2/creditCards";
import { PlusIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import {
  Box,
  Pressable,
  VStack,
  Text,
} from "@whoosh/design-system/src/components/primitive";

import {
  CardBrand,
  ExistingSquareCardsList_customer$key,
} from "./__generated__/ExistingSquareCardsList_customer.graphql";

type Props = {
  selectedCardId?: string;
  setSelectedCardId: (cardId: string) => void;
  customerRef?: ExistingSquareCardsList_customer$key | null;
  onAddNewCard: () => void;
  onCancel: () => void;
};

export const ExistingSquareCardsList = ({
  customerRef,
  onAddNewCard,
  selectedCardId,
  setSelectedCardId,
}: Props) => {
  const customer = useFragment(
    ExistingSquareCardsListFragmentString,
    customerRef
  );

  const cards =
    customer?.squareCards?.edges?.flatMap((e) => (e?.node ? [e.node] : [])) ||
    [];

  return (
    <VStack gap={6}>
      <VStack gap={2}>
        <Text variant="mainBaseSemiBold">{t`Cards on File`}</Text>
        {cards.map((card) => (
          <Pressable
            key={card.id}
            gap={2}
            onPress={() => setSelectedCardId(card.id)}
            flexDirection="row"
            padding={3}
            borderRadius="2"
            borderWidth={1.5}
            borderColor={
              selectedCardId === card.id ? "outline-primary" : "outline-default"
            }
            alignItems="center"
          >
            <Box
              height={24}
              width={32}
              backgroundColor="surface-secondary"
              borderRadius="1"
              justifyContent="center"
              alignItems="center"
            >
              {convertCardBrandToIcon(card.cardBrand)}
            </Box>
            <Text variant="mainSmallSemiBold" color="text-primary">
              {getCardBrandTextValue(card.cardBrand)}
              {"  •••• "}
              {card.lastFour}
            </Text>
            <Text variant="mainSmallSemiBold" color="text-secondary">
              {t`Exp.`} {card.expMonth}
              {"/"}
              {card.expYear.toString().slice(-2)}
            </Text>
          </Pressable>
        ))}
        <Button
          size={cards.length ? "small" : "regular"}
          variant={cards.length ? "secondary" : "main"}
          accessibilityLabel={t`Add card`}
          testID="payment-methods-add-a-card-button"
          onPress={onAddNewCard}
          leftIcon={<PlusIcon />}
          width="100%"
        >{t`Add card`}</Button>
      </VStack>
    </VStack>
  );
};

const ExistingSquareCardsListFragmentString = graphql`
  fragment ExistingSquareCardsList_customer on Customer {
    id
    squareCards(includeUserCards: true, enabled: true) {
      edges {
        node {
          id
          enabled
          cardBrand
          expMonth
          expYear
          lastFour
        }
      }
    }
  }
`;
export const getCardBrandTextValue = (cardBrand?: CardBrand) => {
  switch (cardBrand) {
    case "VISA":
      return "Visa";
    case "AMERICAN_EXPRESS":
      return "American Express";
    case "MASTERCARD":
      return "Mastercard";
    case "DISCOVER":
      return "Discover";
    default:
      return "None";
  }
};
